import { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { useCompanyUsers } from "@/hooks/useCompanyUsers";
import { useProjects } from "@/hooks/useProjects";
import { useAuth } from "@/contexts/AuthContext";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { OnlineIndicator } from "./OnlineIndicator";

interface NewConversationDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSubmit: (data: { title?: string; type: string; memberIds: string[]; projectId?: string }) => void;
  isOnline: (userId: string) => boolean;
}

export const NewConversationDialog = ({ open, onOpenChange, onSubmit, isOnline }: NewConversationDialogProps) => {
  const { user } = useAuth();
  const { data: companyUsers } = useCompanyUsers();
  const { data: projects } = useProjects();
  const [type, setType] = useState<string>("dm");
  const [title, setTitle] = useState("");
  const [selectedUsers, setSelectedUsers] = useState<string[]>([]);
  const [projectId, setProjectId] = useState<string>("");

  const otherUsers = (companyUsers ?? []).filter((u: any) => u.user_id !== user?.id);

  const handleSubmit = () => {
    if (selectedUsers.length === 0) return;
    onSubmit({
      title: type !== "dm" ? title || undefined : undefined,
      type,
      memberIds: selectedUsers,
      projectId: projectId || undefined,
    });
    setType("dm");
    setTitle("");
    setSelectedUsers([]);
    setProjectId("");
    onOpenChange(false);
  };

  const toggleUser = (userId: string) => {
    setSelectedUsers(prev =>
      prev.includes(userId) ? prev.filter(id => id !== userId) : [...prev, userId]
    );
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>New Conversation</DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          <div>
            <Label>Type</Label>
            <Select value={type} onValueChange={setType}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="dm">Direct Message</SelectItem>
                <SelectItem value="group">Group Chat</SelectItem>
                <SelectItem value="project">Project Channel</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {type !== "dm" && (
            <div>
              <Label>Title</Label>
              <Input value={title} onChange={e => setTitle(e.target.value)} placeholder="Conversation name..." />
            </div>
          )}

          {type === "project" && (
            <div>
              <Label>Link to Project</Label>
              <Select value={projectId} onValueChange={setProjectId}>
                <SelectTrigger><SelectValue placeholder="Select project..." /></SelectTrigger>
                <SelectContent>
                  {(projects ?? []).map((p: any) => (
                    <SelectItem key={p.id} value={p.id}>{p.project_number ? `${p.project_number} — ` : ""}{p.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}

          <div>
            <Label>Members</Label>
            <div className="max-h-48 overflow-y-auto space-y-1 mt-1 border rounded-lg p-2">
              {otherUsers.map((u: any) => (
                <label key={u.user_id} className="flex items-center gap-3 py-1.5 px-2 rounded hover:bg-accent cursor-pointer">
                  <Checkbox checked={selectedUsers.includes(u.user_id)} onCheckedChange={() => toggleUser(u.user_id)} />
                  <div className="relative">
                    <Avatar className="h-7 w-7">
                      <AvatarImage src={u.profile_photo_url ?? undefined} />
                      <AvatarFallback className="text-[10px]">{(u.first_name?.[0] ?? "")}{(u.last_name?.[0] ?? "")}</AvatarFallback>
                    </Avatar>
                    <div className="absolute -bottom-0.5 -right-0.5">
                      <OnlineIndicator isOnline={isOnline(u.user_id)} size="sm" />
                    </div>
                  </div>
                  <span className="text-sm">{u.first_name} {u.last_name}</span>
                </label>
              ))}
              {otherUsers.length === 0 && <p className="text-sm text-muted-foreground p-2">No team members found</p>}
            </div>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={handleSubmit} disabled={selectedUsers.length === 0}>Start Chat</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
